import IconAdd from '@apollo/icons/default/IconAdd.svg';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconLock from '@apollo/icons/default/IconLock.svg';
import IconLogout from '@apollo/icons/default/IconLogout.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Avatar,
  AvatarBadge,
  Menu,
  MenuButton,
  MenuContent,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import classnames from 'classnames';
import { partition } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { OutlinkForEmbed } from 'src/app/embedHelpers/OutlinkForEmbed';
import { graphRefToString, useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useIsLoggedIn } from 'src/app/graph/hooks/useIsLoggedIn';
import {
  sandboxExplorerRouteConfig,
  sandboxGraphRouteConfig,
} from 'src/app/graph/routes';
import {
  embedLogoutRouteConfig,
  logoutRouteConfig,
} from 'src/app/onboarding/routes';
import { useGetContactSupportOverlayLocation } from 'src/components/ContactSupportCTAs';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useOrgCreationLocation } from 'src/hooks/useOrgCreationLocation/useOrgCreationLocation';
import { useWindowSize } from 'src/hooks/useWindowSize';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';
import { OrgSwitcherQuery_me_User_memberships } from 'src/lib/graphqlTypes/types';
import { mergeQueryParams } from 'src/lib/routing';

import { MenuSectionHeader } from './MenuSectionHeader';
import { SubMenu } from './SubMenu';

export interface Props {
  orgName?: string;
  orgAvatarUrl?: string;
  orgPlanName?: string;
  orgMemberships?: OrgSwitcherQuery_me_User_memberships[];
  userFullName?: string;
  userAvatarUrl?: string;
  userEmail?: string;
  currentAccountId?: string;
  canCreateOrg?: boolean;
  className?: string;
}

export const SettingsMenu = ({
  orgName,
  orgAvatarUrl,
  orgPlanName,
  orgMemberships = [],
  userFullName,
  userAvatarUrl,
  userEmail,
  currentAccountId,
  canCreateOrg,
  className,
}: Props) => {
  const [visitedLockedOrgs, setVisistedLockedOrgs] = useState<
    Record<string, boolean>
  >({});
  const location = useLocation();
  const { isLoggedIn } = useIsLoggedIn();
  const createNewOrgLocation = useOrgCreationLocation();
  const graphRef = useGraphRef();
  const [windowWidth = 0] = useWindowSize();
  const contactSupportOverlayLocation = useGetContactSupportOverlayLocation();

  const isInSandbox = matchPath(
    location.pathname,
    sandboxGraphRouteConfig.definition,
  );

  const [unlockedOrgs = [], lockedOrgs = []] = orgMemberships.length
    ? partition(orgMemberships, ['account.isLocked', false])
    : [[], []];

  const orgList = useMemo(() => {
    return [...unlockedOrgs, ...lockedOrgs];
  }, [lockedOrgs, unlockedOrgs]);

  const userOrgRole = useMemo(() => {
    return orgList
      .find((membership) => membership?.account?.id === currentAccountId)
      ?.role?.replace('_', ' ')
      ?.toLowerCase();
  }, [currentAccountId, orgList]);

  useEffect(() => {
    if (!currentAccountId) return;

    if (
      !visitedLockedOrgs[currentAccountId] &&
      lockedOrgs.find((org) => org.account.id === currentAccountId)
    ) {
      setVisistedLockedOrgs((prev) => ({
        ...prev,
        [currentAccountId]: true,
      }));
    }
  }, [currentAccountId, lockedOrgs, visitedLockedOrgs]);

  const submenuPlacement = windowWidth <= 638 ? 'bottom-end' : 'right-start';

  return (
    <Menu isLazy>
      <MenuButton className={className} name="settings">
        <Avatar
          name={orgName}
          src={orgAvatarUrl}
          variant="org"
          className="m-auto"
          size="sm"
        >
          <AvatarBadge className="size-[1.5em] border-none">
            <Avatar name={userFullName} src={userAvatarUrl} size="xs" />
          </AvatarBadge>
        </Avatar>
      </MenuButton>
      <MenuContent>
        <MenuList>
          {/* Org Menu Items */}
          <MenuGroup label="ORGANIZATION">
            <div className="flex flex-row items-center justify-start gap-2 text-start">
              <Avatar
                name={orgName}
                src={orgAvatarUrl}
                size="sm"
                variant="org"
              />
              <MenuSectionHeader
                header={orgName}
                subheader={
                  orgPlanName ? (
                    <span className="block w-56 truncate text-sm font-normal text-secondary">
                      Plan: {orgPlanName}
                    </span>
                  ) : null
                }
              />
            </div>
            {!!currentAccountId && (
              <PermissionGuard
                accountId={currentAccountId}
                accountPermissions={[
                  'canQueryBillingInfo',
                  'canSetConstrainedPlan',
                  'canUpdateBillingInfo',
                  'canUpdateMetadata',
                ]}
                fallback={null}
              >
                <MenuItem
                  as={Link}
                  to={orgRoutes.settings.path({
                    orgId: currentAccountId,
                  })}
                >
                  Organization Settings
                </MenuItem>
              </PermissionGuard>
            )}
            {!!currentAccountId && (
              <PermissionGuard
                accountId={currentAccountId}
                accountPermissions="canQueryMembers"
                fallback={null}
              >
                <MenuItem
                  tabIndex={0}
                  as={Link}
                  to={orgRoutes.members.path({
                    orgId: currentAccountId,
                  })}
                >
                  Members
                </MenuItem>
              </PermissionGuard>
            )}
            <SubMenu
              placement={submenuPlacement}
              subMenuBtnLabel="Switch organization"
            >
              <MenuList>
                {orgList.map((m) => {
                  const visitedLockedOrg = !!visitedLockedOrgs[m.account.id];
                  return (
                    <MenuItem
                      key={m.account.id}
                      as={Link}
                      to={orgRoutes.graphs.location({ orgId: m.account.id })}
                      icon={
                        <Avatar
                          name={m.account.name}
                          src={m.account.avatarUrl || undefined}
                          size="xs"
                          variant="org"
                        />
                      }
                      rightIcon={
                        currentAccountId === m.account.id ? (
                          <IconCheck />
                        ) : m.account.isLocked ? (
                          visitedLockedOrg ? (
                            <IconLock />
                          ) : (
                            <IconWarning className="text-icon-warning" />
                          )
                        ) : null
                      }
                    >
                      <span
                        className={classnames({
                          'text-primary': !visitedLockedOrg,
                          'text-secondary':
                            visitedLockedOrg &&
                            m.account.id !== currentAccountId,
                        })}
                      >
                        <span
                          className={classnames(
                            'max-w-36 truncate',
                            currentAccountId === m.account.id && 'font-medium',
                          )}
                        >
                          {m.account.name}
                        </span>
                      </span>
                    </MenuItem>
                  );
                })}
                {canCreateOrg ? (
                  <MenuItem
                    as={Link}
                    to={createNewOrgLocation}
                    icon={<IconAdd />}
                  >
                    Create Org
                  </MenuItem>
                ) : null}
              </MenuList>
            </SubMenu>
          </MenuGroup>
          <MenuDivider />
          <MenuGroup label="ACCOUNT">
            <div className="flex flex-col gap-2 text-start text-base">
              <div className="flex flex-row items-center justify-start gap-2">
                <Avatar name={userFullName} src={userAvatarUrl} size="sm" />
                <MenuSectionHeader
                  header={
                    <>
                      {userFullName}{' '}
                      {userOrgRole ? (
                        <span className="my-auto ml-1 capitalize">
                          ({userOrgRole})
                        </span>
                      ) : null}
                    </>
                  }
                  subheader={userEmail}
                />
              </div>
              <div>
                <MenuItem
                  tabIndex={0}
                  as={OutlinkForEmbed}
                  to={orgRoutes.userSettingsRouteConfig.location({})}
                >
                  Personal Settings
                </MenuItem>
                <MenuItem
                  as={Link}
                  to={{
                    ...location,
                    search: mergeQueryParams(location.search, {
                      [Config.queryParameters.Overlay]:
                        Config.modals.appChangelog,
                    }),
                  }}
                  aria-label="What's new in Studio"
                >
                  What's new in Studio
                </MenuItem>
                <MenuItem
                  as={OutlinkForEmbed}
                  to={orgRoutes.userSettingsRouteConfig.location({
                    hash: '#preview-features',
                  })}
                  aria-label="Preview features"
                >
                  Preview features
                </MenuItem>
                {
                  // if a user is not logged in, they should only see the log in CTA
                  // if a user is on the embedded explorer, they shouldn't see sandbox
                  isLoggedIn && !isEmbeddableExplorerRoute() && (
                    <MenuItem
                      data-analytics-label="Go to Sandbox click on Empty Supergraph Page"
                      data-analytics-category="6M2S: Onboarding"
                      as={OutlinkForEmbed}
                      to={
                        isInSandbox
                          ? '/'
                          : sandboxExplorerRouteConfig.location({})
                      }
                      className="flex"
                      onClick={() => {
                        ampli.clickedLink({
                          'Product Context': 'Graph Onboarding V1',
                          Description: 'Clicked link for Sandbox',
                          'New URL': isInSandbox
                            ? '/'
                            : sandboxExplorerRouteConfig.location({}).pathname,
                          'Previous URL': location.pathname,
                        });
                      }}
                    >
                      Sandbox
                    </MenuItem>
                  )
                }
              </div>
            </div>
          </MenuGroup>
          <MenuDivider />
          <MenuGroup>
            <MenuItem
              as="a"
              href="https://support.apollographql.com/"
              rel="noopener noreferrer"
              target="_blank"
            >
              Help Center
            </MenuItem>
            <MenuItem as={Link} to={contactSupportOverlayLocation}>
              Contact support
            </MenuItem>
          </MenuGroup>
          <MenuDivider />
          <MenuItem
            tabIndex={0}
            as={Link}
            to={
              isEmbeddableExplorerRoute() || isEmbeddableSandboxRoute()
                ? embedLogoutRouteConfig.location({
                    from: `${window.location.pathname}${window.location.search}`,
                    graphRef: graphRefToString(graphRef) ?? undefined,
                  })
                : logoutRouteConfig.location({})
            }
            aria-label="Log out"
            icon={<IconLogout />}
          >
            Log Out
          </MenuItem>
        </MenuList>
      </MenuContent>
    </Menu>
  );
};
